import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  fetchTaste,
  startTaste,
  TASTE_JOB_KEY,
  type TasteResult,
  type TasteState,
  type TasteEvent,
  type TasteStep,
} from '@/services/api/guestTasteApi';
import { joinWaitlist } from '@/services/api/waitlistApi';
import { posthogCapture } from '@/utils/posthogCapture';
import { msSinceLandingMount } from '@/utils/ftueClock';
import { rememberPostLoginPath } from '@/utils/postLoginPath';
import { auth } from '@/config/firebase';
import { cn } from '@/lib/utils';
import type { HeroRunSignal } from './HeroBackground';
import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  RotateCcw,
  ShieldCheck,
} from 'lucide-react';

/**
 * GuestTasteWidget (project100 T1006/T1012 — landing gate v2, D-014/D-015).
 *
 * The doorway: drop a company URL → the engine scans it, ranks 5 investors,
 * and drafts 5 real outreach emails. Every progress state shown here comes
 * verbatim from the backend (state + progress_label) — nothing is simulated.
 * The payoff screen is the signup moment. Its label states what the click actually
 * does ("Sign up free to send" — a mailbox is what sends these), not what we wish it
 * did: T1022/D-018 replace it with "Review these 5 Emails" when the handoff lands (renamed
 * from "Continue with these 5" on 2026-09-27 — one name for one button).
 * Email capture (/request-access) is the mid-scan/error fallback, never the
 * main path (D-011).
 *
 * REOPENED 2026-09-18 (PO, first impression QA): the wait was a three-word
 * state beside a pulsing bar that could not move, so a working engine read as
 * a stall. Two causes, both fixed here and in the service:
 *  1. the backend ALREADY streams progress (raisy_78 `on_progress`: 5 sub-steps
 *     with a real 0-100) and the guest service was not passing the callback, so
 *     the signal was discarded before it reached anyone;
 *  2. the widget then had nothing to show, so it showed a percentage it could
 *     not know and never set an expectation for the wait.
 * The rail, the percent and the "email 3 of 5" counter are all the backend's own
 * numbers — the same honesty rule as the wizard's ScanStatus. The estimate
 * ("usually under two minutes") is a MEASURED time (80-82s across recent live
 * runs), stated up front, so the wait is expected instead of discovered.
 */

type Phase =
  | { kind: 'idle' }
  | {
      kind: 'running';
      jobId: string;
      state: TasteState | null;
      label: string | null;
      /** null = the backend has not reported a percentage — show no number. */
      percent: number | null;
      steps: TasteStep[];
      drafted: number;
      draftTotal: number;
      typicalSeconds: number;
      /**
       * The drafts that have LANDED, in the order they were written (P100 D-028).
       * Rendered as each one arrives, so the wait stops being one line for 84s.
       */
      drafts: TasteResult['drafts'];
      /**
       * Every unit of work the engine has FINISHED, in order (P216 T0204 · @P100 D-029). Optional
       * because not every way into this phase reports it: a run restored from storage, or an
       * engine that predates the list, shows nothing rather than pretending to a progress history.
       */
      events?: TasteEvent[];
      /**
       * FALSE only while a run RESTORED from storage is being checked — nothing about it has been
       * confirmed by the engine yet. Measured 2026-09-27 (PO, on DEV): a saved job rendered the
       * whole working panel — `Starting the engine…`, the three step labels, and an elapsed clock
       * reading **7580s** — while no run existed anywhere. So the clock, the step rail and every
       * "we are working" line are withheld until the engine answers. `undefined` means a run THIS
       * page started, which is confirmed by construction.
       */
      confirmed?: boolean;
    }
  | { kind: 'done'; result: TasteResult }
  | {
      kind: 'error';
      message: string;
      busy: boolean;
      retryAfterSeconds?: number;
      reason?: string;
      /**
       * The ENGINE lost a run that had already produced something (measured 2026-09-28: a deploy
       * restarted the API 62 s into a run, four of five emails already landed). The landed drafts are
       * real `bot_call_logs` rows, stamped with this run's claim token, and the claim takes whatever
       * landed — there is no completeness guard — so the payoff survives the process that wrote it.
       * `landed` is the count the SURFACE WATCHED arrive: never a guess, never the engine's total.
       */
      interrupted?: { landed: number };
    }
  /**
   * ONE FREE SCAN PER BROWSER, THEN IDENTITY (P216 D-003).
   *
   * The doorway promises "free, no signup", and the FIRST run keeps that promise exactly. The
   * second run from the same browser asks who he is — which is the only thing that prices an
   * EXTRACTION of our pool (an anonymous loop harvests it; an attributable account makes that a
   * traceable act). It is a state of its own, never an error: nothing broke and nobody is at fault.
   */
  | { kind: 'signin_required' };

/**
 * THE FREE LANE'S COUNTER — one scan per browser, then identity (P216 D-003).
 *
 * localStorage rather than session, deliberately: a harvester's browser persists across tabs and
 * restarts, and that is the loop this makes attributable. It is freely cleared — which is exactly
 * why the NETWORK ceiling stays behind it as the backstop; neither replaces the other. A cleared
 * storage returns the browser to ONE free scan, never to none.
 */
const FREE_SCANS_KEY = 'raisy_taste_free_scans';

function freeScansUsed(): number {
  try {
    return Number(localStorage.getItem(FREE_SCANS_KEY) ?? '0') || 0;
  } catch {
    return 0;
  }
}

function noteFreeScan(): void {
  try {
    localStorage.setItem(FREE_SCANS_KEY, String(freeScansUsed() + 1));
  } catch {
    /* private mode: the network ceiling is what holds */
  }
}

/**
 * Give the free scan BACK when the engine lost the run.
 *
 * `noteFreeScan()` spends the browser's one free scan the moment a run STARTS, so a run the engine
 * forgets would cost the visitor his scan and make this page's own instruction false: it says *"Run
 * it again and it starts fresh"*, and the retry would meet the identity wall instead. The engine
 * forgets honestly — the job store is the API process's memory with a 1h TTL, so any deploy destroys
 * every in-flight run.
 *
 * MEASURED on DEV 2026-09-28: a real guest journey died this way mid-run — the backend booted at
 * 06:33:26Z, four of five emails had landed (29.1s / 29.7s / 30.2s / 30.7s), and the fifth never came.
 *
 * A refund is not a hole. This counter is client-side and freely clearable by design — `noteFreeScan`'s
 * own comment says so — which is exactly why the NETWORK ceiling (5 jobs per IP per hour) stands
 * behind it as the real guard.
 */
function refundFreeScan(): void {
  try {
    localStorage.setItem(FREE_SCANS_KEY, String(Math.max(0, freeScansUsed() - 1)));
  } catch {
    /* private mode: the network ceiling is what holds */
  }
}

const POLL_EVERY_MS = 2000;
/**
 * How many consecutive failed polls before we tell the guest we lost the engine.
 * 20 x 2s = 40s of patience. The run itself takes 80s, so a 16s dip (the old
 * value of 8) was giving up in the middle of the piece we had asked them to wait
 * for — and a backend restart, a proxy hiccup or a laptop waking up all look the
 * same from the browser. The hiccup line stays visible the whole time, so the
 * patience is never silent.
 */
const MAX_NET_FAILURES = 20;
/**
 * How long a saved run may still be resumed: the engine's OWN retention, mirrored
 * (`guest_taste_service.py` — `JOB_TTL_SECONDS = 3600`). Past it the server has forgotten the job, so
 * resuming would paint progress for a run that cannot be alive — the "7580s so far" the PO saw.
 *
 * DECLARED 2026-09-28. The guard in the resume effect has referenced this name since `d56a41cf`
 * (2026-09-27) without it ever existing anywhere in the repo: a saved run made that effect throw and
 * took the doorway with it. Nothing refused it — the build's `tsc` step compiles 0 of 1,846 files
 * (a solution tsconfig, `"files": []`), which is why `scripts/check_no_broken_names.sh` now runs
 * where that dead step used to.
 */
const JOB_RETENTION_MS = 60 * 60 * 1000;
/** Shown until the first poll lands (2s) — mirrors the engine's own phase plan. */
const INITIAL_STEPS: TasteStep[] = [
  { key: 'reading', label: 'Reading your website', state: 'active' },
  { key: 'ranking', label: 'Ranking the investor pool', state: 'pending' },
  { key: 'drafting', label: 'Writing your 5 emails', state: 'pending' },
];
const DEFAULT_TYPICAL_SECONDS = 90;

/**
 * The drafts outlive the page: a signup navigation must not destroy them.
 *
 * The storage key is NOT defined here — it belongs to the API module that owns the run,
 * because the account surface reads the same key to claim what this page prepared (T1025).
 * Two definitions of one key is exactly the drift this avoids.
 */
interface SavedJob {
  jobId: string;
  /** The secret the claim presents (T1025) — minted with the job, never the job id. */
  claimToken: string;
  startedAt: number;
  url: string;
  stage: string;
}

const saveJob = (job: SavedJob): void => {
  try {
    sessionStorage.setItem(TASTE_JOB_KEY, JSON.stringify(job));
  } catch {
    /* private mode — the run simply stops being resumable */
  }
};

const loadJob = (): SavedJob | null => {
  try {
    const raw = sessionStorage.getItem(TASTE_JOB_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SavedJob;
    return parsed?.jobId ? parsed : null;
  } catch {
    return null;
  }
};

const clearJob = (): void => {
  try {
    sessionStorage.removeItem(TASTE_JOB_KEY);
  } catch {
    /* nothing to do */
  }
};

const normalizeUrl = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const urlHost = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
};

/** Measured, not promised — we only ever claim what the runs show. */
const expectationText = (typicalSeconds: number): string => {
  if (typicalSeconds <= 120) return 'usually under two minutes';
  if (typicalSeconds <= 180) return 'usually about two minutes';
  return `usually about ${Math.round(typicalSeconds / 60)} minutes`;
};

const confidenceText = (value: number | string): string => {
  if (typeof value === 'number') {
    return value <= 1 ? `${Math.round(value * 100)}%` : `${value}`;
  }
  return value;
};

const compliancePassed = (value: Record<string, unknown> | null): boolean =>
  value !== null && value.allowed === true;

const STAGE_OPTIONS = ['pre-seed', 'seed', 'series a', 'series b+'] as const;

const EmailStatusBadge = ({ status }: { status: string }) =>
  status === 'verified' ? (
    <span className="inline-flex items-center gap-1 rounded-full border border-cyan-200 bg-cyan-50 px-2 py-0.5 text-xs font-medium text-cyan-700 dark:border-cyan-800 dark:bg-cyan-900/20 dark:text-cyan-400">
      <BadgeCheck className="h-3 w-3" /> verified email
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
      email {status}
    </span>
  );

/**
 * D-024 (3) — THE SCORE CARRIES ITS MEANING, NOT ONLY ITS NUMBER.
 *
 * `W1` is "stage/industry/vision, not volume", and a bare `fit 70` is a figure nobody can
 * calibrate — so it gets either over-trusted or dismissed, and both are the failure that rule
 * names. The scale and what the figure weighs are therefore stated where the figure is shown.
 * The per-investor detail still lives in `fit_reasons` (the "Why this investor" line on an open
 * draft), which names the signals that actually carried THAT investor.
 *
 * `/100`, never `%`. The scorer returns points on a 0-100 scale (`round(... * 100)` in
 * `investor_fit_scoring_service.py`), so a percent sign would claim a proportion of a population
 * that nothing in it measures.
 *
 * The wording is true for a GUEST specifically: this path scores with `renormalize=True`, so
 * signals whose inputs are absent on either side are dropped and the rest are rescaled — which
 * is why the caption says only the signals that could be read count toward it. Check size is
 * named only in the general caption, since a guest supplies no raise amount.
 */
const FIT_MEANING =
  'how closely published stage, sector and geography match yours, plus how reachable the contact is';

/**
 * THE ONE CTA, IN BOTH STATES (P100 D-026/D-028, PO 2026-09-26).
 *
 * It goes live from the FIRST landed email, not only when all five exist, because the
 * guest no longer has to stay on this page to be served: what is written here is
 * carried into his account and the rest keep being written there.
 *
 * The destination is remembered HERE, by the click that chooses it, because the auth
 * guard cannot see it — it fires during this client-side navigation and reads the URL
 * as `/` (measured on dev 2026-09-23: `raisy_post_login_path` was `/`, and the visitor
 * landed on the default surface instead of his five drafts). This is the only place
 * that knows where the guest was going.
 */
const ContinueCta = ({ startedAt }: { startedAt: number }) => (
  <Link to="/messages" className="block">
    <Button
      size="lg"
      className="h-12 w-full gap-2 bg-[#FD8025] px-6 text-white hover:bg-[#e56f1c]"
      onClick={() => {
        rememberPostLoginPath("/messages");
        posthogCapture("send5_click", { elapsed_ms: Date.now() - startedAt });
      }}
    >
      Review these 5 Emails <ArrowRight className="h-4 w-4" />
    </Button>
  </Link>
);

const FitBadge = ({ score }: { score: number }) => (
  <span
    className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-700 dark:border-teal-800 dark:bg-teal-900/20 dark:text-teal-400"
    title={`Fit ${score} out of 100 — ${FIT_MEANING}`}
    aria-label={`Fit ${score} out of 100 — ${FIT_MEANING}`}
  >
    fit {score}/100
  </span>
);

/**
 * The inline email cell (PO 2026-09-18: "i would expect a cell to enter the
 * email"). The old surface was a LINK away to /request-access, which throws a
 * mid-scan visitor out of the page they are watching — the exact moment they are
 * most interested. Same destination (the one waitlist sheet), no exit.
 *
 * The copy states what actually happens: a human comes back to them. There is no
 * robot that mails drafts today, so the cell must not imply one. `context` is
 * written into the sheet's code column so the row says which site was scanned and
 * the follow-up can actually happen.
 */
  /**
   * WHAT THIS CELL MAY PROMISE IS THE CALLER'S TO SAY (P216 D-008, 2026-09-28).
   *
   * It used to promise "your drafts" in every state it appeared in — including a REFUSAL, where
   * nothing was scanned and nothing was written. That is the strongest form of promising what is
   * not there: persona `W6` (no overpromise) broken at the one moment a wary first-time visitor is
   * deciding whether we are honest, and design implication 7 says a claim must be true at the
   * moment it renders. So the promise is a PROP, and the two callers state it:
   *   · drafts actually landed → "we will come back to you with your drafts" (true: they exist)
   *   · anything else          → "we will write to you when it opens" (true: the list is read)
   * Both are kept by the SAME mechanism (`joinWaitlist`), so the weaker promise costs nothing to
   * keep and is therefore the DEFAULT — the stronger one is asserted only where it is certain.
   */
  const InlineEmailCapture = ({
    context,
    tasteDomain,
    promise = 'Rather not wait? Leave your email and we will write to you when it opens.',
    done = 'Got it. We will write to you when it opens.',
  }: {
    context: string;
    tasteDomain: string;
    promise?: string;
    done?: string;
  }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim();
    if (!value.includes('@')) {
      setStatus('error');
      setMessage('That email does not look right.');
      return;
    }
    setStatus('sending');
    setMessage('');
    try {
      const res = await joinWaitlist('', value, context);
      if (res.status) {
        setStatus('done');
        posthogCapture('taste_email_capture', {});
      } else {
        setStatus('error');
        setMessage(res.message || 'That did not go through. Try again in a moment.');
      }
    } catch {
      setStatus('error');
      setMessage('That did not go through. Try again in a moment.');
    }
  };

  if (status === 'done') {
    return (
      <p className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
        <BadgeCheck className="h-4 w-4 shrink-0" />
        {done}
      </p>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-2" data-testid="taste-email-cell">
      <p className="text-sm text-slate-600 dark:text-slate-300">
        {promise}
      </p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={tasteDomain ? `you@${tasteDomain}` : 'you@company.com'}
          aria-label="Your email address"
          data-testid="taste-email-input"
          className="h-10 flex-1 bg-white text-sm dark:bg-slate-900"
        />
        <Button
          type="submit"
          variant="outline"
          size="sm"
          disabled={status === 'sending'}
          data-testid="taste-email-submit"
          className="h-10 gap-2 px-4"
        >
          {status === 'sending' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Email me
        </Button>
      </div>
      {status === 'error' && (
        <p className="text-xs text-amber-700 dark:text-amber-400">{message}</p>
      )}
    </form>
  );
};

export const GuestTasteWidget = ({
  /** @P100 D-031 option B — the hero's backdrop is the RUN, so the units it has finished are lifted
   *  to the hero. Optional: the doorway works identically with no listener attached. */
  onRunSignal,
}: { onRunSignal?: (signal: HeroRunSignal | null) => void } = {}) => {
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [url, setUrl] = useState('');
  const [stage, setStage] = useState('');
  const [geography, setGeography] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const [netFailures, setNetFailures] = useState(0);
  const startedAtRef = useRef(0);
  /** True once THIS page started a run, so an expired id is only an error then. */
  const startedHereRef = useRef(false);

  /**
   * The drafts the surface has WATCHED land — the only count an interrupted run may promise. Held at
   * its highest for the same reason as the unit count: a poll arriving without the list must not
   * reduce work he saw arrive, and a fresh run starts from a clean zero.
   */
  const landedRef = useRef(0);
  useEffect(() => {
    if (phase.kind === 'running') landedRef.current = Math.max(landedRef.current, phase.drafts.length);
    if (phase.kind === 'idle') landedRef.current = 0;
  }, [phase]);

  /**
   * D-031 option B — the backdrop is told what the ENGINE has finished, from its own event list
   * (D-029: one event per real unit of work). Nothing here is derived: if the engine reports no
   * events, the backdrop is told nothing and stays quiet, because a first screen that invents
   * activity fails the SOURCE/ACCURACY gate that made this option defensible.
   */
  const runUnits = phase.kind === 'running' ? (phase.events?.length ?? 0) : 0;
  /** Held at its highest: a poll that arrives without the list must not un-light work he watched land. */
  const lastUnitsRef = useRef(0);
  useEffect(() => {
    if (!onRunSignal) return;
    if (phase.kind === 'running') {
      lastUnitsRef.current = Math.max(lastUnitsRef.current, runUnits);
      onRunSignal({ units: lastUnitsRef.current, running: true });
      return;
    }
    if (phase.kind === 'done') {
      // Both counts are the engine's own (units it reported, drafts it returned) — the bigger real
      // one wins, so the field never loses work between the last poll and the result.
      lastUnitsRef.current = Math.max(lastUnitsRef.current, phase.result.drafts.length);
      onRunSignal({ units: lastUnitsRef.current, running: false });
      return;
    }
    lastUnitsRef.current = 0;
    onRunSignal(null);
  }, [phase, runUnits, onRunSignal]);

  const runningJobId = phase.kind === 'running' ? phase.jobId : null;
  const isRunning = phase.kind === 'running';
  /**
   * A restored run the engine has not answered about yet. The panel shows that it is CHECKING rather
   * than what it is doing, because until the answer arrives there is nothing true to say about it.
   */
  const unconfirmed = phase.kind === 'running' && phase.confirmed === false;

  // Honest elapsed clock while the engine works.
  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isRunning]);

  // A run survives the page: coming back (e.g. from a signup round-trip) picks the
  // job up again instead of showing an empty doorway and losing the payoff the
  // guest was promised. The engine keeps the job for an hour.
  useEffect(() => {
    const saved = loadJob();
    if (!saved) return;
    // A run older than the engine's retention cannot be alive, so it is DROPPED rather than
    // resumed: the honest zero here is an empty doorway, not a panel of imaginary progress.
    if (Date.now() - saved.startedAt > JOB_RETENTION_MS) {
      clearJob();
      return;
    }
    startedAtRef.current = saved.startedAt;
    setUrl(saved.url);
    setStage(saved.stage);
    setPhase({
      kind: 'running',
      jobId: saved.jobId,
      state: null,
      label: null,
      percent: null,
      steps: INITIAL_STEPS,
      drafted: 0,
      draftTotal: 5,
      typicalSeconds: DEFAULT_TYPICAL_SECONDS,
      drafts: [],
      // THIS page did not start it and the engine has not answered yet — see `confirmed`.
      confirmed: false,
    });
  }, []);

  // Poll the real job — every status shown is the backend's own.
  useEffect(() => {
    if (!runningJobId) return;
    let cancelled = false;

    const poll = async () => {
      const res = await fetchTaste(runningJobId);
      if (cancelled) return;

      if (!res.ok) {
        if (res.kind === 'network') {
          setNetFailures((n) => {
            if (n + 1 >= MAX_NET_FAILURES) {
              setPhase({
                kind: 'error',
                busy: false,
                message:
                  'Lost contact with the engine. Your drafts may still be cooking. Try again, or leave your email below.',
              });
            }
            return n + 1;
          });
          return;
        }
        // An id the engine no longer knows: if we started it on this page that is a
        // real lapse; if we merely resumed it, fall back to the doorway quietly.
        // EITHER WAY HE GETS HIS FREE SCAN BACK — the run was lost by the engine, not spent by him
        // (see refundFreeScan; measured live 2026-09-28, a deploy destroyed a run mid-flight).
        if (res.kind === 'unknown_job') {
          refundFreeScan();
          if (!startedHereRef.current) {
            clearJob();
            setPhase({ kind: 'idle' });
            return;
          }
        }
        setPhase({
          kind: 'error',
          busy: false,
          message: res.message,
          // Only for a job the ENGINE lost after it produced something — a rate-limited or
          // refused request is a different situation and keeps its own honest wording.
          interrupted:
            res.kind === 'unknown_job' && landedRef.current > 0
              ? { landed: landedRef.current }
              : undefined,
        });
        return;
      }

      setNetFailures(0);
      const job = res.job;

      if (job.state === 'done' && job.result) {
        posthogCapture('taste_done', {
          elapsed_ms: Date.now() - startedAtRef.current,
          investors: job.result.investors.length,
          drafts: job.result.drafts.length,
        });
        setPhase({ kind: 'done', result: job.result });
        return;
      }
      if (job.state === 'error') {
        setPhase({
          kind: 'error',
          busy: false,
          message: job.error || 'The engine hit a snag with this scan.',
        });
        return;
      }
      setPhase({
        kind: 'running',
        jobId: runningJobId,
        // The engine has answered about THIS job — from here the panel may claim work.
        confirmed: true,
        state: job.state,
        label: job.progress_label,
        percent: job.progress_percent ?? null,
        steps: job.steps?.length ? job.steps : INITIAL_STEPS,
        drafted: job.drafted ?? 0,
        draftTotal: job.draft_total || 5,
        typicalSeconds: job.typical_seconds || DEFAULT_TYPICAL_SECONDS,
        drafts: job.drafts ?? [],
        // P216 T0204 — the finished units, verbatim from the engine. Never derived here: the FE
        // has no way to know a read finished, and a list it guessed would be the invented
        // activity the rule forbids.
        events: job.events ?? [],
      });
    };

    void poll();
    const id = setInterval(() => void poll(), POLL_EVERY_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [runningJobId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalized = normalizeUrl(url);
    if (!normalized || !urlHost(normalized).includes('.')) {
      setPhase({
        kind: 'error',
        busy: false,
        message: 'That does not look like a company URL. Try something like https://yourcompany.com.',
      });
      return;
    }
    if (!stage) {
      setPhase({
        kind: 'error',
        busy: false,
        message: 'Pick your round stage. The investor match depends on it.',
      });
      return;
    }

    // ONE FREE SCAN PER BROWSER, THEN IDENTITY (P216 D-003). The promise is kept for the first
    // run, exactly as the doorway states it; the second asks who he is, because an anonymous
    // browser can harvest our pool and an attributable one cannot do it quietly. Signed-in
    // visitors never meet this (their ceiling is their account's).
    if (!auth?.currentUser && freeScansUsed() >= 1) {
      posthogCapture('doorway_identity_asked', { url_host: urlHost(normalized) });
      setPhase({ kind: 'signin_required' });
      return;
    }

    startedAtRef.current = Date.now();
    startedHereRef.current = true;
    setNetFailures(0);
    posthogCapture('ftue_entry', {
      time_to_entry_ms: msSinceLandingMount(),
      url_host: urlHost(normalized),
    });
    setPhase({
      kind: 'running',
      jobId: '',
      state: null,
      label: null,
      percent: null,
      steps: INITIAL_STEPS,
      drafted: 0,
      draftTotal: 5,
      typicalSeconds: DEFAULT_TYPICAL_SECONDS,
      drafts: [],
    });

    const res = await startTaste(normalized, {
      stage,
      geography: geography.trim() || undefined,
    });
    if (!res.ok) {
      startedHereRef.current = false;
      setPhase({
        kind: 'error',
        busy: res.kind === 'rate_limited',
        message: res.message,
        retryAfterSeconds: res.retryAfterSeconds,
        reason: res.reason,
      });
      return;
    }
    // The run STARTED: this browser has now spent its free scan.
    noteFreeScan();
    // Persisted BEFORE the first poll: if the guest leaves for a signup round-trip
    // (or reloads), the run is resumable instead of lost.
    saveJob({
      jobId: res.jobId,
      claimToken: res.claimToken,
      startedAt: startedAtRef.current,
      url: normalized,
      stage,
    });
    setPhase({
      kind: 'running',
      jobId: res.jobId,
      state: null,
      label: null,
      percent: null,
      steps: INITIAL_STEPS,
      drafted: 0,
      draftTotal: 5,
      typicalSeconds: DEFAULT_TYPICAL_SECONDS,
      drafts: [],
    });
  };

  const retry = () => {
    clearJob();
    startedHereRef.current = false;
    setPhase({ kind: 'idle' });
  };

  const elapsedSec = isRunning && !unconfirmed
    ? Math.max(0, Math.floor((nowTick - startedAtRef.current) / 1000))
    : 0;
  /** Recorded on the waitlist row so a follow-up knows which site was scanned. */
  const sheetContext = `GUEST_TASTE:${urlHost(normalizeUrl(url)) || 'unknown'}`;
  /** He already typed his domain — the email cell uses it instead of a generic hint. */
  const tasteDomain = urlHost(normalizeUrl(url)).replace(/^www\./, '');

  return (
    <div className="mx-auto w-full max-w-2xl">
      {/* ——— Input: the doorway ——— */}
      {(phase.kind === 'idle' || phase.kind === 'error') && (
        <form onSubmit={submit} noValidate className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row">
            <Input
              type="text"
              inputMode="url"
              autoComplete="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://yourcompany.com"
              aria-label="Your company URL"
              className="h-12 flex-1 bg-white px-4 text-base shadow-sm dark:bg-slate-900"
            />
            <Button
              type="submit"
              size="lg"
              className="h-12 gap-2 bg-cyan-600 px-6 text-white hover:bg-cyan-700"
            >
              Draft my 5 emails <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
          {/* PO 2026-09-23 — "remove (optional)". What replaced it is the reason the row is
              worth filling, in his terms, not ours (P100 D-023).

              "Need to know" was the PO's first suggestion and it was REJECTED on the
              persona's announcement gate (W11.7): it tells the visitor that WE need
              something, never what the row IS, and over two fields of which only one is
              required it reads as a demand for both — untrue at the moment it renders (W6).
              The rule it fails is the pair of rejected names in that same want: a label is
              judged by what it does to the reader, and the reader here is being helped or
              he is being billed.

              "For sharper matches" states what he gains and compels nothing, which is the
              PO's other constraint in the same sentence: the field improves the ranking
              (T1014 — the scorer reads stage and geography, and without them the fit
              collapsed to 8-10/100) but it stays non-compulsory; geography still submits as
              undefined when left blank and the round remains the only required field. */}
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            For sharper matches
          </p>
          {/* T0400 · IN-237-01/02 — THE TWO ROWS NAME THEMSELVES, VISIBLY.
              Simon circled both fields and could not tell what either was: *"Does this mean
              geography? or what kind of investor? … Need an unambiguous description here"*.
              The PO settled which meaning was missing: *"its not clear that it means
              geography"*. Only the placeholder and an aria-label named them, and a name a
              reader has to open the field to find has FAILED the announcement gate
              (`@RULES_NAMING §6` gate 2).

              Both gates, both fields (`D-237-013`):
              · GATE 1 (positivity): each names what the visitor HAS — his round, where he
                raises. Neither has an absence as its subject, and the forbidden-list grep
                is clean.
              · GATE 2 (announcement): *Your funding round* removes both wrong readings
                Simon had (geography · what kind of investor); *Where you raise* plus the
                placeholder *city or country* names the PLACE, which is the half that was
                missing.
              · GATE 2b: the select's first option names what it asks for (*choose your
                round*), never a bare verb.
              · The heading above them is untouched: *For sharper matches* is the one the
                PO chose in `@P100 D-023`, it states the gain and compels nothing, and it
                stays true for both fields.
              ⚠️ The aria-labels are REMOVED rather than kept: with a visible label, a
              differing aria-label would give one control two names (`@RULES_NAMING §4`) and
              would override the visible text for a screen reader. The eight journey tests
              that selected this control BY ITS WORDING now use a `data-testid` — a test that
              pins copy blocks the next copy fix, which is the class this row was born from. */}
          <div className="flex flex-col gap-3 sm:flex-row">
            <label className="flex flex-1 flex-col gap-1">
              <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
                Your funding round
              </span>
              <select
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                data-testid="taste-round"
                required
                className="h-12 w-full rounded-md border border-input bg-white px-4 text-base text-slate-900 shadow-sm outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] dark:bg-slate-900 dark:text-white"
              >
                <option value="" disabled>
                  choose your round
                </option>
                {STAGE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-1 flex-col gap-1">
              <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
                Where you raise
              </span>
              <Input
                type="text"
                value={geography}
                onChange={(e) => setGeography(e.target.value)}
                placeholder="city or country"
                data-testid="taste-geography"
                maxLength={80}
                className="h-12 w-full bg-white px-4 text-base shadow-sm dark:bg-slate-900"
              />
            </label>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Free, no signup. It reads your site, ranks 5 investors and writes 5 emails, usually in
            under two minutes.
          </p>
        </form>
      )}

      {/* ——— ONE FREE SCAN PER BROWSER, THEN IDENTITY (P216 D-003) ———
           Not an error and never coloured like one: nothing failed and nobody is at fault. The
           promise the doorway makes ("free, no signup") is kept for the first run, exactly as
           stated; the second asks who he is, which is what makes an anonymous harvest of our pool
           an attributable act. The wording names the RULE rather than the visitor's shortfall. */}
      {phase.kind === 'signin_required' && (
        <div
          role="status"
          data-testid="taste-signin-required"
          className="mt-4 rounded-lg border border-slate-200 bg-white/80 p-4 text-left shadow-sm backdrop-blur dark:border-slate-700 dark:bg-slate-900/80"
        >
          <div className="space-y-3">
            <p className="text-sm font-medium text-slate-900 dark:text-white">
              Every browser gets one scan without an account.
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              This one has had its run. Sign in and the doorway keeps going — and the five emails
              from your scan come with you.
            </p>
            <Button asChild size="sm" className="gap-2">
              <Link to="/login" onClick={() => rememberPostLoginPath('/')}>
                Sign in to run another <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </Button>
          </div>
        </div>
      )}

      {/* ——— Error: honest message + retry + email fallback ——— */}
      {phase.kind === 'error' && (
        <div
          role="alert"
          className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-left dark:border-amber-900/50 dark:bg-amber-950/30"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="space-y-3">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                {phase.interrupted
                  ? 'The engine restarted while it was writing.'
                  : phase.reason === 'capacity'
                    ? "The doorway has reached today's capacity."
                    : phase.busy
                      ? 'The doorway has run its scans for now.'
                      : 'The scan did not go through.'}
              </p>
              {phase.interrupted ? (
                /* WHAT LANDED IS KEPT. "Run it again" is true but wasteful: the drafts are real and claimable,
                   so the sentence leads with what he still gets and names the re-run as the option, not the
                   instruction. The count is what the surface WATCHED land. */
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  {phase.interrupted.landed} of 5 emails had already landed, and they are saved. Sign in and
                  they are yours — or run the scan again for all five.
                </p>
              ) : (
                <p className="text-sm text-amber-700 dark:text-amber-400">{phase.message}</p>
              )}
              {/* WHEN, NOT "LATER". The guard measures its own window, and a first-time
                  visitor who is told an actual number can decide whether to wait — the same
                  expectation-management rule the running panel follows. */}
              {phase.busy && phase.retryAfterSeconds ? (
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  It opens again in about{' '}
                  {Math.max(1, Math.round(phase.retryAfterSeconds / 60))}{' '}
                  {Math.round(phase.retryAfterSeconds / 60) === 1 ? 'minute' : 'minutes'}.
                </p>
              ) : null}
              <div className="flex flex-wrap items-center gap-3">
              {phase.interrupted ? (
                /* The same door the payoff itself uses (D-018/T1022): sign in, land on the mail
                   surface, and the claim takes what landed. Deliberately the same CTA rather than a
                   second mechanism — one promise, one way to keep it. */
                <Link to="/login" onClick={() => rememberPostLoginPath('/messages')}>
                  <Button type="button" size="sm" className="gap-2 bg-cyan-600 text-white hover:bg-cyan-700">
                    <BadgeCheck className="h-3.5 w-3.5" /> Keep these {phase.interrupted.landed}
                  </Button>
                </Link>
              ) : null}
                {/* P216 D-008 — A CONTROL THAT CANNOT SUCCEED IS NOT OFFERED. While the guard's
                    own window is shut, "Try again" can only refuse him a second time, and the
                    sentence directly above already says when it opens. The button therefore steps
                    aside in that one state and stays for every state where a retry can genuinely
                    work (a lost job, a capacity refusal, a dropped connection). */}
                {phase.busy ? null : (
                  <Button type="button" variant="outline" size="sm" onClick={retry} className="gap-2">
                    <RotateCcw className="h-3.5 w-3.5" /> Try again
                  </Button>
                )}
              </div>
              {/* AND THE PROMISE MATCHES WHAT ACTUALLY HAPPENED. "Your drafts" is true ONLY where
                  drafts landed; under a refusal there are none, so the honest promise is being
                  written to when the doorway opens — the one the list can always keep. */}
              <InlineEmailCapture
                context={sheetContext}
                tasteDomain={tasteDomain}
                {...(phase.interrupted
                  ? {
                      promise:
                        'Rather not wait? Leave your email and we will come back to you with your drafts.',
                      done: 'Got it. We will come back to you with your drafts.',
                    }
                  : {})}
              />
            </div>
          </div>
        </div>
      )}

      {/* ——— Running: the engine's own progress, on a rail, with the wait named ——— */}
      {phase.kind === 'running' && (
        <div
          data-testid="taste-progress"
          className="rounded-xl border border-slate-200 bg-white/80 p-6 text-left shadow-sm backdrop-blur dark:border-slate-700 dark:bg-slate-900/80"
        >
          {/* The phase rail — the same shape as the entity wizard's ProgressRail.
              Every dot state comes from the backend's own phase plan. */}
          {/* The rail is the engine's OWN phase plan, so it is shown only once the engine has
              spoken about this run: three labelled steps before that would be a plan we are
              attributing to a run nobody has confirmed (PO, 2026-09-27). */}
          {!unconfirmed && (
            <ol className="flex items-start" data-testid="taste-rail">
            {phase.steps.map((step, i) => {
              const done = step.state === 'done';
              const active = step.state === 'active';
              const isLast = i === phase.steps.length - 1;
              return (
                <li
                  key={step.key}
                  data-testid="taste-step"
                  data-phase={step.key}
                  data-state={step.state}
                  className={cn('flex items-center', !isLast && 'flex-1')}
                >
                  <div className="flex shrink-0 flex-col items-center gap-1.5">
                    <div
                      className={cn(
                        'flex h-5 w-5 items-center justify-center rounded-full text-xs font-semibold transition-all duration-300',
                        done
                          ? 'bg-cyan-600 text-white'
                          : active
                            ? 'border-[1.5px] border-cyan-500 text-cyan-600 ring-2 ring-cyan-500/10 dark:text-cyan-400'
                            : 'border border-slate-200 text-slate-400 dark:border-slate-700 dark:text-slate-500',
                      )}
                    >
                      {done ? <Check className="h-2.5 w-2.5 stroke-[3]" /> : i + 1}
                    </div>
                    <span
                      className={cn(
                        'hidden whitespace-nowrap text-xs font-medium leading-none sm:block',
                        active
                          ? 'text-cyan-600 dark:text-cyan-400'
                          : 'text-slate-500 dark:text-slate-400',
                      )}
                    >
                      {step.label}
                    </span>
                  </div>
                  {!isLast && (
                    <div className="relative mx-1.5 mt-[-11px] h-px flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                      <div
                        className="absolute inset-y-0 left-0 bg-cyan-600 transition-all duration-500 ease-out"
                        style={{ width: done ? '100%' : '0%' }}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
          )}

          {/* A real percentage when the engine reports one. When it does not, an
              honest indeterminate bar — never a number we do not have. */}
          <div className="mt-6 flex items-center gap-3">
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-cyan-600 dark:text-cyan-400" />
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
              {phase.percent === null ? (
                <div className="h-full w-1/3 animate-pulse rounded-full bg-gradient-to-r from-cyan-500 to-teal-400" />
              ) : (
                <div
                  className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-teal-400 transition-[width] duration-700 ease-out"
                  style={{ width: `${Math.max(2, phase.percent)}%` }}
                />
              )}
            </div>
            {phase.percent !== null && (
              <span
                data-testid="taste-percent"
                data-percent={phase.percent}
                className="w-9 shrink-0 text-right text-xs tabular-nums text-slate-500 dark:text-slate-400"
              >
                {phase.percent}%
              </span>
            )}
          </div>

          {/* What is happening now, in the engine's own words, plus the wait named. */}
          <p
            data-testid="taste-label"
            className="mt-3 text-sm font-medium text-slate-900 dark:text-white"
          >
            {unconfirmed ? 'Checking on your scan…' : (phase.label ?? 'Starting the engine…')}
          </p>
          <p
            data-testid="taste-expectation"
            className="mt-1 text-xs text-slate-500 dark:text-slate-400"
          >
            {unconfirmed ? (
              // No clock and no step rail until the engine says there is a run: a timer is a claim
              // about how long something has been happening.
              <>Picking up the scan you started — checking with the engine.</>
            ) : (
              <>
                {phase.state === 'drafting' && phase.drafted > 0
                  ? `${phase.drafted} of ${phase.draftTotal} written · `
                  : ''}
                {elapsedSec}s so far · {expectationText(phase.typicalSeconds)}. You can leave this
                page whenever you like — your drafts continue in your Email Drafts.
                {netFailures > 0 ? ' · connection hiccup, retrying' : ''}
              </>
            )}
          </p>

          {/* THE DRAFTS AS THEY LAND (P100 D-028). Until now this wait was one line for
              84 seconds and the payoff existed only at the end. Each email the engine
              finishes is rendered here immediately, and the CTA goes live with the FIRST
              one — so the page can be left at any moment without losing anything.

              THE BUTTON IS ON TOP (PO 2026-09-26: *"the button should be visible after the
              first email is visible … Secondly the button should be on top"*). It used to
              render UNDER the list it belongs to, so on a phone the way out of a wait —
              *take all five with you now* — sat below everything already written and had to
              be scrolled to. The sentence explains the button and stays with it, above the
              list: read what is ready, or leave with the lot. */}
          {/* ── P216 T0204 — WHAT THE ENGINE HAS ALREADY FINISHED (@P100 D-029) ─────────────────
              The rail says WHERE WE ARE; this says WHAT IS DONE, one line per unit the backend
              recorded as finished, with the elapsed seconds it finished at. Every line is a real
              completion — never a plan and never a percentage, because a progress bar asked a
              visitor to trust arithmetic and a finished unit asks him to trust his own eyes.
              ⚠️ The `draft` events are deliberately NOT rendered here: the list below IS them, with
              the emails attached, and one fact shown twice teaches a reader that one of the two is
              decoration. This lists what the drafts cannot say — the company found and the pool
              scored. */}
          {(phase.events ?? []).some((e) => e.kind !== 'draft') && (
            <ul className="mt-4 space-y-1" data-testid="taste-done-trail">
              {(phase.events ?? [])
                .filter((e) => e.kind !== 'draft')
                .map((e, i) => (
                  <li key={`${e.kind}-${i}`} className="flex items-baseline gap-2 text-xs">
                    <span aria-hidden="true" className="text-emerald-600 dark:text-emerald-400">
                      ✓
                    </span>
                    <span className="text-slate-600 dark:text-slate-300">{e.label}</span>
                    <span className="tabular-nums text-slate-400 dark:text-slate-500">
                      {e.at.toFixed(1)}s
                    </span>
                  </li>
                ))}
            </ul>
          )}
          {phase.drafts.length > 0 && (
            <div className="mt-5 border-t border-slate-100 pt-4 dark:border-slate-800">
              <p
                data-testid="taste-live-guidance"
                className="text-xs text-slate-500 dark:text-slate-400"
              >
                {phase.drafts.length} of {phase.draftTotal} ready — read them here, or take all five
                with you now. The rest keep being written in your Email Drafts.
              </p>
              <div className="mt-3">
                <ContinueCta startedAt={startedAtRef.current} />
              </div>
              <ul className="mt-3 space-y-2" data-testid="taste-live-drafts">
                {phase.drafts.map((draft, i) => {
                  const open = expanded === i;
                  return (
                    <li
                      key={`${draft.investor_name}-${i}`}
                      className="rounded-lg border border-slate-200 bg-white/80 backdrop-blur dark:border-slate-700 dark:bg-slate-900/80"
                    >
                      <button
                        type="button"
                        onClick={() => setExpanded(open ? null : i)}
                        aria-expanded={open}
                        className="flex w-full items-center gap-2 px-4 py-3 text-left"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-slate-900 dark:text-white">
                            {draft.investor_name}
                          </span>
                          <span className="mt-0.5 block truncate text-xs text-slate-500 dark:text-slate-400">
                            {draft.subject}
                          </span>
                          <span className="mt-1 flex flex-wrap gap-1.5">
                            <FitBadge score={draft.fit_score} />
                            <EmailStatusBadge status={draft.confidence.email_status} />
                          </span>
                        </span>
                        {open ? (
                          <ChevronUp className="h-4 w-4 shrink-0 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                        )}
                      </button>
                      {open && (
                        <div className="border-t border-slate-100 px-4 py-3 dark:border-slate-800">
                          <p className="whitespace-pre-line text-sm text-slate-600 dark:text-slate-300">
                            {draft.body}
                          </p>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="mt-5 border-t border-slate-100 pt-4 dark:border-slate-800">
            <InlineEmailCapture context={sheetContext} tasteDomain={tasteDomain} />
          </div>
        </div>
      )}

      {/* ——— Done: the payoff ——— */}
      {phase.kind === 'done' && (
        <div className="space-y-6 text-left">
          {/* Company summary */}
          <div className="rounded-xl border border-slate-200 bg-white/80 p-5 shadow-sm backdrop-blur dark:border-slate-700 dark:bg-slate-900/80">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                {phase.result.company.name}
              </h3>
              <span className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-700 dark:border-teal-800 dark:bg-teal-900/20 dark:text-teal-400">
                read confidence {confidenceText(phase.result.company.extraction_confidence)}
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              {phase.result.company.description}
            </p>
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              {[
                phase.result.company.industry,
                phase.result.company.geography,
                phase.result.company.raising_stage,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>

          {/* THE WAY IN SITS ABOVE THE EMAILS, NOT UNDER THEM (PO 2026-09-27: *"put the button to
              the top ABOVE the email area.. the user shouldnt scroll down to discover the button,
              he should get this button visibly in the vertical scrolling area where he was"*). It
              used to render after all five emails AND the honest-math paragraph, so the one control
              that carries them into his account was the last thing on the page. The sentence that
              explains the button travels WITH it, exactly as it does in the running panel. */}
          <div>
            <ContinueCta startedAt={startedAtRef.current} />
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              A free account carries these five into your company, where each one waits for your
              decision. Your own mailbox is what sends them after that.
            </p>
          </div>

          {/* The drafts. ONE list: the investor and their email are the same thing
              to the guest, and the old layout printed the investor twice — once
              under "5 investors matched", again inside every email row. */}
          <div>
            <h4
              data-testid="taste-drafts-heading"
              className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
            >
              Your {phase.result.drafts.length} drafted emails
            </h4>
            <p className="mb-2 mt-1 text-xs text-slate-500 dark:text-slate-400">
              Written for each investor. Nothing has been sent yet. Open one to read it.{' '}
              <span data-testid="taste-fit-meaning">
                Fit is out of 100 — it weighs published stage, sector and geography against
                yours, plus how reachable the contact is, and only the signals that could be read
                count toward it.
              </span>
              {typeof phase.result.pool_size === 'number' && phase.result.pool_size > 0
                ? ` Ranked against ${phase.result.pool_size} investors we could read.`
                : ''}
            </p>
            <ul className="space-y-2" data-testid="taste-drafts">
              {phase.result.drafts.map((draft, i) => {
                const open = expanded === i;
                return (
                  <li
                    key={`${draft.investor_name}-${i}`}
                    className="rounded-lg border border-slate-200 bg-white/80 backdrop-blur dark:border-slate-700 dark:bg-slate-900/80"
                  >
                    <button
                      type="button"
                      onClick={() => setExpanded(open ? null : i)}
                      aria-expanded={open}
                      className="flex w-full items-center gap-2 px-4 py-3 text-left"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-slate-900 dark:text-white">
                          {draft.investor_name}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-slate-500 dark:text-slate-400">
                          {draft.subject}
                        </span>
                        <span className="mt-1 flex flex-wrap gap-1.5">
                          <FitBadge score={draft.fit_score} />
                          <EmailStatusBadge status={draft.confidence.email_status} />
                          {compliancePassed(draft.confidence.compliance) ? (
                            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400">
                              <ShieldCheck className="h-3 w-3" /> compliance passed
                            </span>
                          ) : draft.confidence.compliance === null ? (
                            <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
                              compliance gate not configured
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-400">
                              gate check at send
                            </span>
                          )}
                        </span>
                      </span>
                      {open ? (
                        <ChevronUp className="h-4 w-4 shrink-0 text-slate-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                      )}
                    </button>
                    {open && (
                      <div className="border-t border-slate-100 px-4 py-3 dark:border-slate-800">
                        <p className="whitespace-pre-line text-sm text-slate-600 dark:text-slate-300">
                          {draft.body}
                        </p>
                        {draft.fit_reasons.length > 0 && (
                          <p className="mt-3 border-t border-slate-100 pt-3 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
                            Why this investor: {draft.fit_reasons.join(' · ')}
                          </p>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Benchmarks — honest expectations, INCLUDING what this list is not.
              T1023: the doorway was quoting cold reply rates as if they were the
              whole answer. Maya's file (W8, which overrides W5) makes warm the
              default and cold the residue, so the page has to say which one this is
              — and that the warm search is the next thing, not a claim about now. */}
          <p className="text-sm text-slate-500 dark:text-slate-400">
            The honest math: these are cold approaches, and well-targeted cold outreach replies at{' '}
            {phase.result.benchmarks.reply_rate_pct}%; 200 reachouts ≈{' '}
            {phase.result.benchmarks.replies_per_200} replies. Warm introductions do far better than
            that, and finding a warm path is the next thing Raisy does with your company once it is in
            your account. These 5 are your start.
          </p>

          <button
            type="button"
            onClick={retry}
            className="text-xs font-medium text-cyan-600 hover:text-cyan-700 dark:text-cyan-400 dark:hover:text-cyan-300"
          >
            Scan another company
          </button>
        </div>
      )}
    </div>
  );
};
